import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredRole?: string;
  fallbackPath?: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  requiredRole,
  fallbackPath = '/auth'
}) => {
  const { loading, initialized, isAuthenticated, hasRole, roles } = useAuth();
  const location = useLocation();

  if (loading || !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth) {
    const raw = sessionStorage.getItem('quiquoiouquand_auth_session');
    const ephemeralOk = (() => {
      if (!raw) return false;
      try { const s = JSON.parse(raw); return !!s?.authenticated; } catch { return false; }
    })();

    // Si ni authentifié ni session éphémère, rediriger vers login
    if (!isAuthenticated() && !ephemeralOk) {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
    
    // Si session éphémère OK, on continue sans redirection
  }

  // If user is authenticated but shouldn't be (e.g., on auth page)
  if (!requireAuth && isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  // If a specific role is required
  if (requiredRole) {
    // Vérification de la session éphémère
    const raw = sessionStorage.getItem('quiquoiouquand_auth_session');
    let ephemeralRole: string | null = null;
    let ephemeralAuthenticated = false;

    try {
      const stored = JSON.parse(raw || '{}');
      if (stored?.authenticated) {
        ephemeralAuthenticated = true;
        // PRIORITÉ: userType est le rôle EXPLICITEMENT sélectionné
        ephemeralRole =
          stored?.userType ||
          stored?.profile?.role ||
          stored?.user?.role ||
          (Array.isArray(stored?.profile?.roles) && stored.profile.roles.length > 0
            ? stored.profile.roles[0]
            : null) ||
          null;
      }
    } catch (e) {
      // Session éphémère invalide
    }

    const normalizedRequired = requiredRole.toLowerCase();
    const normalizedEphemeral = (ephemeralRole || '').toLowerCase();
    
    // NOUVELLE LOGIQUE: Si l'utilisateur a une session éphémère avec un rôle sélectionné,
    // on vérifie que ce rôle correspond au rôle requis pour cette page
    // Cela empêche un utilisateur connecté en tant que "prestataire" d'accéder au dashboard admin
    // même s'il a le rôle admin en base de données
    
    const hasDbRole = roles.length > 0 ? hasRole(normalizedRequired as any) : false;
    const hasEphemeralRole = ephemeralAuthenticated && normalizedEphemeral === normalizedRequired;

    logger.log('[AuthGuard] Role check', {
      path: location.pathname,
      requiredRole,
      roles,
      ephemeralRole,
      hasDbRole,
      hasEphemeralRole,
    });

    // Si une session éphémère existe avec un rôle sélectionné différent du rôle requis,
    // rediriger vers le bon dashboard plutôt que de bloquer
    if (ephemeralAuthenticated && ephemeralRole && normalizedEphemeral !== normalizedRequired) {
      const correctDashboard = 
        normalizedEphemeral === 'admin' ? '/dashboard-admin' :
        normalizedEphemeral === 'prestataire' ? '/dashboard-prestataire' :
        normalizedEphemeral === 'client' ? '/dashboard-client' : null;
      
      if (correctDashboard && location.pathname !== correctDashboard) {
        logger.log('[AuthGuard] Redirection vers le dashboard du rôle sélectionné:', correctDashboard);
        return <Navigate to={correctDashboard} replace />;
      }
    }

    // Si pas de session éphémère, on utilise les rôles de la DB
    if (!hasDbRole && !hasEphemeralRole) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold">Accès restreint</p>
            <p className="text-sm text-muted-foreground">
              Votre compte n'a pas le rôle requis.
            </p>
            <a href="/auth" className="inline-block underline">Se reconnecter</a>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
